import type { ControllerRenderProps } from 'react-hook-form';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import { PolicyMode } from '@kirin/api-client/contracts/policies';
import type { PolicySettingsFormData } from '../types';
import {
  MODE_SUB_POLICY_ENFORCE_TOOLTIP,
  MODE_SUB_POLICY_NOTE,
  modeSubPolicyDisabledNote,
  narrowPolicyMode,
} from '../constants';
import { ModeSettingField } from './ModeSettingField';
import { useParentPolicyMode } from './parentPolicyMode';

interface ModeSettingCardProps {
  setting: PolicySetting;
  field: ControllerRenderProps<PolicySettingsFormData, string>;
  readOnly?: boolean;
}

/**
 * Card layout for a nested mode sub-policy (KIR-4426).
 *
 * Unlike the generic setting row — which hides its description behind a help
 * icon and stretches the control across the full width — a sub-policy that
 * carries its own Off/Monitor/Enforce decision needs the admin to read what
 * it does before choosing, so the description sits in the card and the
 * selector shrinks to sit beside it.
 *
 * KIR-4858: the parent policy is still a master switch. With the parent Off,
 * neither the backend (`computeToolCallScanMode`) nor the agent
 * (`ToolCallScanModeOf`) will scan whatever is stored here, so the control is
 * disabled AND displayed as Off rather than left lit on a selection that is
 * not in effect. The displayed Off is cosmetic only — `field.value` keeps the
 * stored mode, so turning the parent back on restores the admin's choice
 * instead of a settings save silently writing Off over it. The note names the
 * kept mode so that value is visible rather than lying in wait.
 */
export function ModeSettingCard({
  setting,
  field,
  readOnly = false,
}: ModeSettingCardProps) {
  const { mode: parentMode, name: parentName } = useParentPolicyMode();
  // An absent parent mode means "no gate provided" (a field rendered in
  // isolation), not "Off" — only an explicit Off disables.
  const parentOff = parentMode === PolicyMode.Off;
  // The PENDING form value, not the persisted one: an unsaved edit made before
  // the parent was switched Off is still what a save would write, so that is
  // the mode the note has to name.
  const disabledNote = modeSubPolicyDisabledNote(
    parentName,
    narrowPolicyMode(field.value)
  );

  const selector = (
    <ModeSettingField
      field={field}
      disabled={readOnly || parentOff}
      displayValue={parentOff ? PolicyMode.Off : undefined}
      size="sm"
      enforceTooltip={MODE_SUB_POLICY_ENFORCE_TOOLTIP}
    />
  );

  return (
    <div className="space-y-1.5">
      <div className="rounded-lg border border-blue-100 bg-blue-50/40 px-4 py-3 dark:border-blue-900/60 dark:bg-blue-950/20">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-semibold" htmlFor={setting.key}>
                {setting.name}
              </Label>
              <Badge
                variant="secondary"
                className="bg-blue-100 text-[10px] font-semibold tracking-wide text-blue-700 dark:bg-blue-900 dark:text-blue-300"
              >
                NEW
              </Badge>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {setting.description}
            </p>
          </div>
          <div className="shrink-0">
            {parentOff ? (
              // The disabled selector sets `pointer-events-none` on its own
              // tab list, so the hover target has to be this wrapper.
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-not-allowed">
                      {selector}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{disabledNote}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              selector
            )}
          </div>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {parentOff ? disabledNote : MODE_SUB_POLICY_NOTE}
      </p>
    </div>
  );
}
